/**
 * The ONE banner treatment for molecule.dev (strips, cards, and fixed toasts).
 *
 * Every dismissable message bar in the app renders through this component —
 * {@link GuestReminder}, {@link UsageNudge}, {@link BudgetResetBanner},
 * {@link NotificationBanner}, {@link HealthBanner}, and the Dashboard's
 * create-error banner. They used to be five hand-rolled near-copies whose
 * layout, button styling, and dismiss placement silently drifted (the mobile
 * "banners look like shit" bug class); this file is the single source of truth
 * for how a banner looks, so a design change lands everywhere at once.
 *
 * Layout contract (identical on every surface):
 * - **Content is centered.** The message, and the action row beneath it, are
 *   horizontally centered in the banner. Symmetric horizontal padding reserves
 *   the dismiss button's column on BOTH sides so "centered" is true centering.
 * - **One sentence per line.** A string message is split at sentence
 *   boundaries and each sentence renders as an `inline-block` chunk: sentences
 *   share a line when they fit (wide desktop strips) but wrap as whole units —
 *   on a 390px phone each sentence gets its own line instead of ragged
 *   mid-sentence breaks. Copy should be short plain sentences, not em-dash
 *   compounds (AGENTS.md rule 13).
 * - **Actions share the message's line when they fit** (wide desktop strips);
 *   when they don't, the whole action group wraps together onto its own
 *   centered line. Every action is a real design-system button — the same
 *   `cm.button` classes the auth page and Dashboard use, at the compact `sm`
 *   size — with a per-action semantic `color` (Sign up `primary` beside Log
 *   in `success`, mirroring the auth page), so banner buttons stay consistent
 *   with every other button in the IDE.
 * - **The × dismiss is pinned top-right** in every variant and breakpoint,
 *   with an enlarged (40×40) hit box on phones.
 *
 * Tone colors follow the P5-10a convention: translucent theme-token washes for
 * the background/border and the readable `--color-*-text` mixes for the
 * foreground, so every tone reads in both themes with no hardcoded hex.
 * `appearance="solid"` is the high-alarm exception (a near-solid fill with
 * white text) used by the unreachable-server banner.
 *
 * Positioning stays with the CALLER: fixed placement, z-index, margins, and
 * width constraints come in via `style`, since where a banner sits is page
 * layout, not banner design.
 *
 * WIRING CONTRACT: this component calls `getClassMap()` at render, which
 * throws until the UI/ClassMap bonds are wired (bonds/ui.ts). Pages loaded via
 * App.tsx's `lazyPage` are always safe. Any AMBIENT surface mounted outside
 * those routes (rendered at App level, on every route incl. the landing page)
 * must gate its render on `useUiProviders(needed)` — rendering un-wired threw
 * "No UIClassMap has been set" and crashed the whole page via the App
 * ErrorBoundary.
 */

import { type CSSProperties, Fragment, type ReactNode } from 'react'
import { Link } from 'react-router'

import { useTranslation } from '@molecule/app-react'
import { type ColorVariant, getClassMap } from '@molecule/app-ui'

import { useIsMobile } from '../hooks/useMediaQuery.js'

/** Semantic tone of a {@link Banner} — decides the wash/border/text colors. */
export type BannerTone = 'info' | 'success' | 'warning' | 'error'

/** One action button rendered on the banner's action row. */
export interface BannerAction {
  label: string
  /** Plain anchor href (external links, /signup-style full-page routes). */
  href?: string
  /** SPA route rendered as a react-router Link. */
  to?: string
  onClick?: () => void
  /** Open `href` in a new tab (adds rel=noreferrer). */
  external?: boolean
  /**
   * Semantic button color (the design system's `cm.button` palette — the same
   * one the auth page and Dashboard buttons draw from). Defaults to
   * `primary`. Give sibling actions different semantics where it reads better
   * (e.g. Sign up `primary` beside Log in `success`, mirroring the auth page).
   */
  color?: ColorVariant
  molId?: string
}

export interface BannerProps {
  tone: BannerTone
  /**
   * `strip` — a full-width bar (border-bottom only) mounted under a header.
   * `card` — a free-standing rounded box (full border) for page content flow.
   */
  variant?: 'strip' | 'card'
  /** `solid` = near-solid alarm fill with white text (unreachable-server). */
  appearance?: 'tinted' | 'solid'
  /**
   * Strips only: which edge carries the 1px border — `bottom` (default, for
   * strips under a header) or `top` (for bars docked to the viewport bottom).
   */
  edge?: 'top' | 'bottom'
  /** A string is split into one-sentence-per-line chunks; nodes render as-is. */
  message: ReactNode
  actions?: BannerAction[]
  /** When present, renders the top-right × and calls this on click. */
  onDismiss?: () => void
  molId?: string
  dismissMolId?: string
  role?: 'alert' | 'status'
  /** Positioning/layout overrides (fixed placement, maxWidth, margins…). */
  style?: CSSProperties
  /** Extra content rendered below the action row (rare). */
  children?: ReactNode
}

const TONE_STYLES: Record<BannerTone, { bg: string; border: string; fg: string }> = {
  info: {
    bg: 'color-mix(in srgb, var(--color-primary) 12%, transparent)',
    border: 'color-mix(in srgb, var(--color-primary) 30%, transparent)',
    fg: 'var(--color-primary, #4070e0)',
  },
  success: {
    bg: 'color-mix(in srgb, var(--color-success) 10%, transparent)',
    border: 'color-mix(in srgb, var(--color-success) 30%, transparent)',
    fg: 'var(--color-success-text, #1a6600)',
  },
  warning: {
    bg: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
    border: 'color-mix(in srgb, var(--color-warning) 30%, transparent)',
    fg: 'var(--color-warning-text, #7a6200)',
  },
  error: {
    bg: 'color-mix(in srgb, var(--color-error) 12%, transparent)',
    border: 'color-mix(in srgb, var(--color-error) 30%, transparent)',
    fg: 'var(--color-error-text, #b81c00)',
  },
}

/**
 * Split display copy into sentence chunks (western + CJK sentence-final
 * punctuation). Presentation-only: locales whose copy doesn't match simply
 * render as one chunk and wrap normally, so this can never lose text.
 * @param text - The banner message.
 * @returns The sentence chunks, in order.
 */
export function splitSentences(text: string): string[] {
  return text.split(/(?<=[.!?。！？])\s+/).filter((s) => s.length > 0)
}

export function Banner({
  tone,
  variant = 'strip',
  appearance = 'tinted',
  edge = 'bottom',
  message,
  actions,
  onDismiss,
  molId,
  dismissMolId,
  role = 'status',
  style,
  children,
}: BannerProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  const toneStyle = TONE_STYLES[tone]
  const solid = appearance === 'solid'
  const bg = solid ? `color-mix(in srgb, var(--color-${tone}) 90%, transparent)` : toneStyle.bg
  const border = solid
    ? `color-mix(in srgb, var(--color-${tone}) 90%, transparent)`
    : toneStyle.border
  const fg = solid ? '#fff' : toneStyle.fg

  // Banner actions are REAL design-system buttons: the same `cm.button` classes
  // (tinted fill + readable `*-text` color + tinted border, hover states) the
  // auth page and Dashboard use, at the compact `sm` size, with the COMPACT
  // 36px coarse-pointer floor (`touchTargetCompact`) — the full 44px
  // `touchTarget` visibly overwhelms a thin banner strip. Styling lives
  // entirely in the ClassMap — no inline colors — so banner buttons can never
  // drift from the rest of the IDE's buttons. `textDecoration:'none'` is the
  // one inline property (anchors default to underline; `cm.button` doesn't
  // manage text-decoration).
  const cm = getClassMap()
  const actionClasses = (color: ColorVariant): string =>
    cm.cn(cm.button({ color, size: 'sm' }), cm.touchTargetCompact)
  const actionStyle: CSSProperties = { textDecoration: 'none' }

  const renderAction = (action: BannerAction, index: number): ReactNode => {
    const className = actionClasses(action.color ?? 'primary')
    if (action.to) {
      return (
        <Link
          key={index}
          to={action.to}
          data-mol-id={action.molId}
          onClick={action.onClick}
          className={className}
          style={actionStyle}
        >
          {action.label}
        </Link>
      )
    }
    if (action.href) {
      return (
        <a
          key={index}
          href={action.href}
          data-mol-id={action.molId}
          onClick={action.onClick}
          className={className}
          style={actionStyle}
          {...(action.external ? { target: '_blank', rel: 'noreferrer noopener' } : {})}
        >
          {action.label}
        </a>
      )
    }
    return (
      <button
        key={index}
        type="button"
        data-mol-id={action.molId}
        onClick={action.onClick}
        className={className}
      >
        {action.label}
      </button>
    )
  }

  return (
    <div
      role={role}
      data-mol-id={molId}
      style={{
        position: 'relative',
        flexShrink: 0,
        // A wrapping centered row: the message and the action group share ONE
        // line when they fit (wide desktop strips); when they don't, the whole
        // action group wraps together onto its own centered line (phones).
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px 12px',
        // Symmetric horizontal inset: the × lives in the right inset and the
        // matching left inset balances it, so the centered content is TRULY
        // centered. Without a dismiss the sides still match.
        padding: onDismiss
          ? variant === 'card'
            ? '12px 44px'
            : '7px 44px'
          : variant === 'card'
            ? '12px 20px'
            : '7px 16px',
        background: bg,
        ...(variant === 'card'
          ? { border: `1px solid ${border}`, borderRadius: 12 }
          : edge === 'top'
            ? { borderTop: `1px solid ${border}` }
            : { borderBottom: `1px solid ${border}` }),
        color: fg,
        fontSize: 13,
        lineHeight: 1.5,
        textAlign: 'center',
        ...style,
      }}
    >
      {/* maxWidth lets a one-line (nowrap + ellipsis) chunk shrink to the
          banner instead of overflowing it — a flex item never shrinks below
          its content otherwise. */}
      <span style={{ maxWidth: '100%' }}>
        {typeof message === 'string'
          ? // One sentence per line: inline-block chunks share a line when they
            // fit and wrap as whole units when they don't (phones), instead of
            // breaking mid-sentence. The separator space sits BETWEEN the
            // inline-blocks (inside one it would collapse away).
            splitSentences(message).map((sentence, i) => (
              <Fragment key={i}>
                {i > 0 && ' '}
                <span style={{ display: 'inline-block' }}>{sentence}</span>
              </Fragment>
            ))
          : message}
      </span>
      {actions && actions.length > 0 && (
        <span
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {actions.map(renderAction)}
        </span>
      )}
      {children}
      {onDismiss && (
        <button
          type="button"
          data-mol-id={dismissMolId}
          onClick={onDismiss}
          aria-label={t('common.dismiss', undefined, { defaultValue: 'Dismiss' })}
          // mol-bespoke-button: icon-only × dismiss, no label — absolutely
          // positioned in the banner's top-right corner, which a button box
          // would frame. Colour is the banner's own `fg` token, and the 44px
          // coarse-pointer target now comes from cm.touchTarget (DESIGN.md
          // reserves the 44px floor for exactly this: × dismiss controls). It
          // used to be a hand-rolled 40×40 — the one Close/dismiss on the
          // platform undershooting the 44px MobileSheet, PanelModal and the
          // workspace toolbar all use.
          className={cm.touchTarget}
          style={{
            // Top-right, ALWAYS — every banner puts its × in the same place.
            position: 'absolute',
            background: 'none',
            border: 'none',
            color: fg,
            cursor: 'pointer',
            fontSize: 16,
            lineHeight: 1,
            opacity: 0.7,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...(isMobile ? { top: 0, right: 0, padding: 0 } : { top: 5, right: 6, padding: 4 }),
          }}
        >
          {'×'}
        </button>
      )}
    </div>
  )
}
